import React, { useState } from 'react';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';

const IpLookup = () => {
  const [enrichedData, setEnrichedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({
    processed: 0, total: 0, estimatedTime: 0, timeRemaining: 0
  });
  const [showModal, setShowModal] = useState(false);

  // Parse response JSON safely
  const parseFormResponse = (jsonStr = '') => {
    try {
      const obj = JSON.parse(jsonStr);
      return {
        first_name: obj.names?.first_name || '',
        last_name: obj.names?.last_name || '',
        email: obj.email || '',
        phone: obj.phone || ''
      };
    } catch {
      return {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      };
    }
  };

  // Enrich IPs in batches
  const batchFetchGeo = async (rows, batchSize = 100) => {
    const enriched = [];
    const total = rows.length;
    const start = Date.now();
  
    for (let i = 0; i < total; i += batchSize) {
      const batch = rows.slice(i, i + batchSize);
  
      // Prepare IP-only list for API request
      const ipList = batch.map(row => ({ query: row.ip || '' }));
  
      let geoResults = [];
      try {
        const res = await fetch('http://ip-api.com/batch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ipList)
        });
  
        geoResults = await res.json();
      } catch (error) {
        console.error("IP batch request failed:", error);
        // If batch fails, fill with empty geo data
        geoResults = Array(batch.length).fill({});
      }
  
      // Merge form response data + IP geolocation data
      const enrichedBatch = batch.map((row, idx) => {
        const geo = geoResults[idx] || {};
        const parsedForm = parseFormResponse(row.response);
        return {
          ip: row.ip || '',
          ...parsedForm,
          city: geo.city || '',
          state: geo.regionName || '',
          country: geo.country || '',
          zip: geo.zip || '',
          timezone: geo.timezone || '',
          status: geo.status || 'fail' // useful for debugging bad IPs
        };
      });
  
      enriched.push(...enrichedBatch);
  
      const processed = Math.min(i + batchSize, total);
      const elapsed = (Date.now() - start) / 1000;
      const rate = processed / elapsed;
      const remaining = total - processed;
      const timeRemaining = remaining / (rate || 1);
  
      setProgress({
        processed,
        total,
        estimatedTime: elapsed + timeRemaining,
        timeRemaining
      });
  
      // Small delay to stay within fair use (optional)
      if (i + batchSize < total) {
        await new Promise(res => setTimeout(res, 1000)); // delay only between batches
      }
    }
  
    return enriched;
  };  

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setShowModal(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const cleanedRows = results.data.map(row => {
          const cleaned = {};
          for (const key in row) {
            cleaned[key.trim()] = (row[key] || '').toString().trim();
          }
          return cleaned;
        });

        const relevantRows = cleanedRows.filter(row => row.response || row.ip);

        if (relevantRows.length === 0) {
          alert("No valid records found with 'response' or 'ip'.");
          setLoading(false);
          setShowModal(false);
          return;
        }

        setProgress({
          processed: 0,
          total: relevantRows.length,
          estimatedTime: (relevantRows.length / 100) * 1.5,
          timeRemaining: (relevantRows.length / 100) * 1.5,
        });

        const enriched = await batchFetchGeo(relevantRows);
        setEnrichedData(enriched);

        const csv = Papa.unparse(enriched);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `enriched_data_${Date.now()}.csv`);

        setLoading(false);
      },
      error: (error) => {
        console.error("CSV Parsing failed:", error);
        alert("CSV parsing error.");
        setLoading(false);
        setShowModal(false);
      }
    });
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-semibold mb-6">📄 CSV Enrichment Tool</h2>
      <input
        type="file"
        accept=".csv"
        onChange={handleFileUpload}
        className="mb-6 block w-full text-sm text-gray-700 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {!loading && enrichedData.length > 0 && (
        <p className="text-green-600 font-medium">✅ {enrichedData.length} records enriched and saved.</p>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm text-center">
            <h3 className="text-xl font-bold mb-4">🔄 Processing Records</h3>
            <div className="text-gray-700 text-sm mb-1">
              Processing {progress.processed} of {progress.total}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-3 overflow-hidden">
              <div
                className="bg-blue-500 h-4 transition-all"
                style={{ width: `${(progress.processed / progress.total) * 100 || 0}%` }}
              ></div>
            </div>
            <div className="text-xs text-gray-600 mb-1">
              Estimated Total Time: <strong>{progress.estimatedTime.toFixed(1)}s</strong><br />
              Time Remaining: <strong>{progress.timeRemaining.toFixed(1)}s</strong>
            </div>
            {progress.processed === progress.total && (
              <button
                onClick={() => setShowModal(false)}
                className="mt-4 w-full bg-green-600 text-white py-2 rounded hover:bg-green-700"
              >
                ✅ Done - Close
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IpLookup;